/**
 * Trending Papers — HuggingFace Daily Papers (community-curated arXiv).
 *
 * What working researchers are reading right now. Sorted by upvotes; refreshed
 * every 6h. Each entry has the AI-generated summary + keyword tags from HF
 * plus links to arXiv, the HF paper page, and (when available) the GitHub repo.
 */
import { useEffect, useMemo, useState } from 'react'
import { FileText, RefreshCw, ExternalLink, Github, ThumbsUp, Filter } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Paper {
  id: string
  title: string
  summary: string
  ai_summary: string
  ai_keywords: string[]
  published_at: string | null
  submitted_at: string | null
  upvotes: number
  comments: number
  github_repo: string | null
  github_stars: number
  project_page: string | null
  thumbnail: string | null
  organization: string
  authors: string[]
  arxiv_url: string | null
  hf_url: string | null
}

export default function TrendingPapersPage() {
  const [papers, setPapers] = useState<Paper[]>([])
  const [fetchedAt, setFetchedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [search, setSearch] = useState('')

  const load = (refresh = false) => {
    setLoading(true)
    api.get('/api/frontier/daily-papers', { params: { refresh } })
      .then(r => {
        setPapers(r.data?.papers || [])
        setFetchedAt(r.data?.fetched_at || null)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load(false) }, [])

  const allKeywords = useMemo(() => {
    const s = new Set<string>()
    papers.forEach(p => p.ai_keywords?.forEach(k => s.add(k)))
    return Array.from(s).sort()
  }, [papers])

  const filtered = useMemo(() => papers
    .filter(p =>
      (!keyword || p.ai_keywords?.includes(keyword)) &&
      (!search  || `${p.title} ${p.organization} ${p.authors.join(' ')}`.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0)),
  [papers, keyword, search])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <FileText size={18} className="text-blue-light" /> TRENDING PAPERS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          huggingface daily · what researchers read · community-upvoted
        </span>
        {fetchedAt && (
          <span className="text-[10px] text-text-muted font-mono">
            updated {fetchedAt.slice(0, 16).replace('T', ' ')} UTC
          </span>
        )}
        <button type="button" onClick={() => load(true)} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Force refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Papers today"   value={papers.length}                                  tone="blue" />
        <Tile label="Total upvotes"  value={papers.reduce((s, p) => s + (p.upvotes || 0), 0)} tone="amber" />
        <Tile label="With code"      value={papers.filter(p => p.github_repo).length}        tone="emerald" />
        <Tile label="Filtered shown" value={filtered.length}                                 tone="purple" />
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={keyword} onChange={e => setKeyword(e.target.value)}
          aria-label="Filter by keyword">
          <option value="">All keywords</option>
          {allKeywords.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <input className="input flex-1 min-w-[200px]"
          placeholder="Search title / org / author"
          value={search} onChange={e => setSearch(e.target.value)} aria-label="Search papers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map(p => (
          <article key={p.id} className="card p-4">
            <div className="flex items-start gap-3">
              {p.thumbnail && (
                <img src={p.thumbnail} alt="" loading="lazy"
                  className="w-20 h-20 rounded shrink-0 object-cover bg-surface-raised" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <span className="text-[11px] font-mono text-amber-light shrink-0">
                    {(p.published_at || p.submitted_at || '').slice(0, 10) || '—'}
                  </span>
                  {p.organization && (
                    <span className="text-[10px] text-purple-light">{p.organization}</span>
                  )}
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-amber-light font-mono"
                    title={`${p.upvotes} upvotes / ${p.comments} comments`}>
                    <ThumbsUp size={10} /> {p.upvotes}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-text-primary leading-snug mb-1">
                  {p.hf_url ? (
                    <a href={p.hf_url} target="_blank" rel="noreferrer" className="hover:text-blue-light">
                      {p.title}
                    </a>
                  ) : p.title}
                </h3>
                {p.authors.length > 0 && (
                  <div className="text-[10px] text-text-muted mb-2 truncate">
                    {p.authors.slice(0, 3).join(', ')}{p.authors.length > 3 ? ` +${p.authors.length - 3}` : ''}
                  </div>
                )}
                <p className="text-xs text-text-secondary leading-relaxed">
                  {p.ai_summary || p.summary}
                </p>
                {p.ai_keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.ai_keywords.slice(0, 6).map(k => (
                      <button key={k} type="button" onClick={() => setKeyword(k === keyword ? '' : k)}
                        className={clsx('badge text-[9px] cursor-pointer transition-colors',
                          k === keyword
                            ? 'bg-purple/30 text-purple-light border border-purple/50'
                            : 'bg-surface-raised text-text-muted hover:bg-purple/10')}>
                        {k}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-2 text-[10px]">
                  {p.arxiv_url && (
                    <a href={p.arxiv_url} target="_blank" rel="noreferrer"
                      className="text-blue-light hover:underline inline-flex items-center gap-1">
                      arXiv <ExternalLink size={9} />
                    </a>
                  )}
                  {p.github_repo && (
                    <a href={p.github_repo} target="_blank" rel="noreferrer"
                      className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
                      <Github size={10} /> {p.github_stars > 0 ? `${p.github_stars}★` : 'code'}
                    </a>
                  )}
                  {p.project_page && (
                    <a href={p.project_page} target="_blank" rel="noreferrer"
                      className="text-emerald hover:underline">
                      project page
                    </a>
                  )}
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Source: <a href="https://huggingface.co/papers" target="_blank" rel="noreferrer"
          className="text-blue-light hover:underline">HuggingFace Daily Papers</a> · refreshed every 6h.
      </p>
    </div>
  )
}

function Tile({ label, value, tone }: { label: string; value: number; tone: 'blue' | 'amber' | 'emerald' | 'purple' }) {
  const c = tone === 'blue' ? 'text-blue-light'
          : tone === 'amber' ? 'text-amber-light'
          : tone === 'emerald' ? 'text-emerald'
          : 'text-purple-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>{value.toLocaleString()}</div>
    </div>
  )
}
